import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShieldCheck, Upload, FileText, Languages, ArrowLeft, Loader2, 
  CheckCircle, User, Mail, Phone, Briefcase, Globe, Clock, XCircle,
  DollarSign, Target, Award, TrendingUp, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BecomeAgent() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const cvInputRef = useRef(null);
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [registrationType, setRegistrationType] = useState(null); // 'new' or 'invited'
  const [existingRequest, setExistingRequest] = useState(null); // Existing agent request
  const [cooldown, setCooldown] = useState({ enabled: false, days: 0 }); // Reapply cooldown from admin
  
  const [formData, setFormData] = useState({
    fullName: '',
    fullNameEn: '',
    email: '',
    phone: '',
    languages: [],
    skills: [],
    experience: '',
    invitationCode: ''
  });
  
  const [cvFile, setCvFile] = useState(null);
  const [cvFileName, setCvFileName] = useState('');
  const [englishNameError, setEnglishNameError] = useState('');
  const [skillInput, setSkillInput] = useState('');

  const availableLanguages = [
    { id: 'ar', name: 'العربية', nameEn: 'Arabic' },
    { id: 'en', name: 'الإنجليزية', nameEn: 'English' },
    { id: 'ur', name: 'الأردية', nameEn: 'Urdu' },
    { id: 'hi', name: 'الهندية', nameEn: 'Hindi' },
    { id: 'fa', name: 'الفارسية', nameEn: 'Persian' },
    { id: 'fr', name: 'الفرنسية', nameEn: 'French' },
    { id: 'zh', name: 'الصينية', nameEn: 'Chinese' },
    { id: 'ru', name: 'الروسية', nameEn: 'Russian' }
  ];

  useEffect(() => {
    const userData = localStorage.getItem('business_user');
    const token = localStorage.getItem('business_token');
    
    if (!userData || !token) {
      navigate('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    
    // Function to check if text contains Arabic characters
    const containsArabic = (text) => /[\u0600-\u06FF]/.test(text);
    // Function to check if text is primarily English (Latin characters)
    const isPrimarilyEnglish = (text) => {
      const latinChars = (text.match(/[a-zA-Z]/g) || []).length;
      const arabicChars = (text.match(/[\u0600-\u06FF]/g) || []).length;
      return latinChars > arabicChars;
    };
    
    // Pre-fill form with user data - put name in correct field based on language
    const userName = parsedUser.name || '';
    
    // If name is primarily English, put in English field
    // If name contains Arabic, put in Arabic field
    const isEnglishName = isPrimarilyEnglish(userName);
    
    setFormData(prev => ({
      ...prev,
      fullName: !isEnglishName ? userName : '', // Arabic name field
      fullNameEn: isEnglishName ? userName : '', // English name field
      email: parsedUser.email || '',
      phone: parsedUser.phone || ''
    }));
    
    // Check if user already has an agent request
    fetchExistingRequest(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchExistingRequest = async (token) => {
    try {
      // Fetch existing request + reapply-cooldown settings in parallel
      const [reqRes, cdRes] = await Promise.allSettled([
        axios.get(`${BACKEND_URL}/api/agent-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/agent-invites/reapply-cooldown`),
      ]);

      if (cdRes.status === 'fulfilled') {
        const d = cdRes.value?.data || {};
        setCooldown({ enabled: !!d.enabled, days: Number(d.days) || 0 });
      }

      if (reqRes.status === 'fulfilled') {
        const response = reqRes.value;
        if (response.data && response.data.length > 0) {
          const latestRequest = response.data[response.data.length - 1];
          if (latestRequest.status === 'approved') {
            navigate('/agent-tasks');
            return;
          }
          setExistingRequest(latestRequest);
        }
      } else {
        console.error('Failed to fetch agent requests:', reqRes.reason);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCvUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error(language === 'ar' ? 'حجم الملف يجب أن يكون أقل من 10 ميجابايت' : 'File size must be less than 10MB');
        return;
      }
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(language === 'ar' ? 'يرجى رفع ملف PDF أو Word' : 'Please upload a PDF or Word file');
        return;
      }
      setCvFile(file);
      setCvFileName(file.name);
    }
  };

  const toggleLanguage = (langId) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(langId)
        ? prev.languages.filter(l => l !== langId)
        : [...prev.languages, langId]
    }));
  };

  // Validate English name - only allow English letters and spaces
  const handleEnglishNameChange = (e) => {
    const value = e.target.value;
    const englishOnlyRegex = /^[A-Za-z\s]*$/;
    
    if (englishOnlyRegex.test(value)) {
      setFormData({ ...formData, fullNameEn: value });
      setEnglishNameError('');
    } else {
      setEnglishNameError(language === 'ar' 
        ? 'يرجى إدخال الحروف الإنجليزية فقط (A-Z)'
        : 'Please enter English letters only (A-Z)'
      );
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.fullName || !formData.fullNameEn || !formData.email || !formData.phone) {
      toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }

    // Validate English name
    const englishOnlyRegex = /^[A-Za-z\s]+$/;
    if (!englishOnlyRegex.test(formData.fullNameEn)) {
      toast.error(language === 'ar' ? 'الاسم الإنجليزي يجب أن يحتوي على حروف إنجليزية فقط' : 'English name must contain only English letters');
      return;
    }

    if (formData.languages.length === 0) {
      toast.error(language === 'ar' ? 'يرجى اختيار لغة واحدة على الأقل' : 'Please select at least one language');
      return;
    }

    if (formData.skills.length === 0) {
      toast.error(language === 'ar' ? 'يرجى إضافة مهارة واحدة على الأقل' : 'Please add at least one skill');
      return;
    }

    if (registrationType === 'new' && !cvFile) {
      toast.error(language === 'ar' ? 'يرجى رفع السيرة الذاتية' : 'Please upload your CV');
      return;
    }

    if (registrationType === 'invited' && !formData.invitationCode) {
      toast.error(language === 'ar' ? 'يرجى إدخال كود الدعوة' : 'Please enter the invitation code');
      return;
    }

    const token = localStorage.getItem('business_token');
    setSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('fullName', formData.fullName);
      submitData.append('fullNameEn', formData.fullNameEn);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('languages', JSON.stringify(formData.languages));
      submitData.append('skills', JSON.stringify(formData.skills));
      submitData.append('experience', formData.experience);
      submitData.append('registrationType', registrationType);
      
      if (registrationType === 'new' && cvFile) {
        submitData.append('cv', cvFile);
      }
      
      if (registrationType === 'invited') {
        submitData.append('invitationCode', formData.invitationCode);
      }

      const response = await axios.post(`${BACKEND_URL}/api/agent-requests`, submitData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const result = response?.data || {};
      const isApproved = result.status === 'approved';
      const reqNo = result.request_number || result.id?.slice(0, 8);

      if (isApproved) {
        toast.success(
          language === 'ar'
            ? `تمت الموافقة بنجاح ✅ — رقم الطلب: ${reqNo}`
            : `Approved successfully ✅ — Request No: ${reqNo}`,
          { duration: 6000 }
        );
      } else {
        toast.success(
          language === 'ar'
            ? `طلبك قيد الانتظار والمراجعة ⏳ — رقم الطلب: ${reqNo}`
            : `Your request is pending review ⏳ — Request No: ${reqNo}`,
          { duration: 6000 }
        );
      }
      navigate('/dashboard');
    } catch (error) {
      // 401 handled globally by axios interceptor (session expired toast + redirect to /login)
      if (error?.response?.status !== 401) {
        toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل إرسال الطلب' : 'Failed to submit request'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center cgm-page`}>
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-[#38BDF8]/20 border-t-[#38BDF8] animate-spin" />
        </div>
      </div>
    );
  }

  // ── Reapply Cooldown helpers (driven by admin settings) ────────────────
  // Cooldown OFF from admin  → agent can re-submit as many times as needed
  // Cooldown ON              → block "New Registration" for `days` after rejection
  const cooldownActive = cooldown.enabled && cooldown.days > 0;
  const rejectedAtMs = existingRequest?.rejected_at ? new Date(existingRequest.rejected_at).getTime() : null;
  const cooldownEndMs = rejectedAtMs ? rejectedAtMs + cooldown.days * 24 * 60 * 60 * 1000 : null;
  const isInCooldown = !!(
    cooldownActive &&
    existingRequest?.status === 'rejected' &&
    cooldownEndMs &&
    Date.now() < cooldownEndMs
  );
  const daysRemaining = isInCooldown
    ? Math.max(1, Math.ceil((cooldownEndMs - Date.now()) / (24 * 60 * 60 * 1000)))
    : 0;
  // Localized "days remaining" label with Arabic pluralization (يوم / يومان / X أيام)
  const daysRemainingLabel = (() => {
    if (!isInCooldown) return '';
    if (language === 'ar') {
      if (daysRemaining === 1) return 'باقي يوم واحد';
      if (daysRemaining === 2) return 'باقي يومان';
      if (daysRemaining <= 10) return `باقي ${daysRemaining} أيام`;
      return `باقي ${daysRemaining} يوماً`;
    }
    return `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} remaining`;
  })();

  return (
    <div className={`min-h-screen cgm-page`}>
      {/* Header - Clean & Subtle */}
      <section className={`relative overflow-hidden py-4 sm:py-5 ${
        isDarkMode 
          ? 'bg-gradient-to-r from-[#1E293B] to-[#0F172A] border-b border-white/5' 
          : 'bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9]'
      }`}>
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-0 end-0 w-64 h-64 rounded-full bg-[#38BDF8] blur-3xl" />
          <div className="absolute bottom-0 start-0 w-48 h-48 rounded-full bg-[#38BDF8] blur-3xl" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={() => navigate('/dashboard')}
            className={`inline-flex items-center mb-3 text-sm transition-colors ${
              isDarkMode ? 'text-gray-400 hover:text-white' : 'text-white/80 hover:text-white'
            }`}
          >
            <ArrowLeft className="h-4 w-4 me-1 rtl:rotate-180" />
            {language === 'ar' ? 'العودة للوحة التحكم' : 'Back to Dashboard'}
          </button>
          
          <div className={`flex items-center gap-4 ${isDarkMode ? 'text-white' : 'text-white'}`}>
            <div className={`p-3 rounded-xl backdrop-blur-sm ${
              isDarkMode ? 'bg-[#38BDF8]/20' : 'bg-white/20'
            }`}>
              <ShieldCheck className={`h-8 w-8 ${isDarkMode ? 'text-[#38BDF8]' : ''}`} />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">
                {language === 'ar' ? 'سجل كوكيل معتمد' : 'Become an Authorized Agent'}
              </h1>
              <p className={isDarkMode ? 'text-gray-400 mt-1' : 'text-white/80 mt-1'}>
                {language === 'ar' ? 'انضم لفريق الوكلاء المعتمدين' : 'Join the authorized agents team'}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 -mt-6">
        {/* Show Existing Request Status - MOVED TO TOP */}
        {existingRequest && (
          <div className="mb-6">
            <div className={`rounded-2xl p-6 shadow-lg border-2 ${
              existingRequest.status === 'pending' 
                ? isDarkMode ? 'bg-orange-500/10 border-orange-500/30' : 'bg-orange-50 border-orange-200'
                : existingRequest.status === 'approved'
                ? isDarkMode ? 'bg-green-500/10 border-green-500/30' : 'bg-green-50 border-green-200'
                : isDarkMode ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${
                  existingRequest.status === 'pending' 
                    ? isDarkMode ? 'bg-orange-500/20' : 'bg-orange-100'
                    : existingRequest.status === 'approved'
                    ? isDarkMode ? 'bg-green-500/20' : 'bg-green-100'
                    : isDarkMode ? 'bg-red-500/20' : 'bg-red-100'
                }`}>
                  {existingRequest.status === 'pending' && <Clock className="h-8 w-8 text-orange-600" />}
                  {existingRequest.status === 'approved' && <CheckCircle className="h-8 w-8 text-green-600" />}
                  {existingRequest.status === 'rejected' && <XCircle className="h-8 w-8 text-red-600" />}
                </div>
                <div className="flex-1">
                  <h3 className={`text-lg font-bold mb-1 ${
                    existingRequest.status === 'pending' 
                      ? isDarkMode ? 'text-orange-400' : 'text-orange-800'
                      : existingRequest.status === 'approved'
                      ? isDarkMode ? 'text-green-400' : 'text-green-800'
                      : isDarkMode ? 'text-red-400' : 'text-red-800'
                  }`}>
                    {existingRequest.status === 'pending' && (language === 'ar' ? 'طلبك قيد المراجعة' : 'Your Request is Under Review')}
                    {existingRequest.status === 'approved' && (language === 'ar' ? 'تم قبول طلبك!' : 'Your Request is Approved!')}
                    {existingRequest.status === 'rejected' && (language === 'ar' ? 'تم رفض طلبك' : 'Your Request was Rejected')}
                  </h3>
                  <p className={`text-sm ${
                    existingRequest.status === 'pending' 
                      ? isDarkMode ? 'text-orange-300' : 'text-orange-600'
                      : existingRequest.status === 'approved'
                      ? isDarkMode ? 'text-green-300' : 'text-green-600'
                      : 'text-red-600'
                  }`}>
                    {existingRequest.status === 'pending' && (language === 'ar' 
                      ? 'سيتم مراجعة طلبك من قبل فريق الإدارة وإشعارك بالنتيجة قريباً. عادة ما تستغرق المراجعة من 1-3 أيام عمل.'
                      : 'Your request will be reviewed by our admin team. You will be notified of the result soon. Review usually takes 1-3 business days.'
                    )}
                    {existingRequest.status === 'approved' && (language === 'ar' 
                      ? 'مبروك! أنت الآن وكيل معتمد لدى CloudGM.'
                      : 'Congratulations! You are now an authorized CloudGM agent.'
                    )}
                    {existingRequest.status === 'rejected' && (language === 'ar' 
                      ? 'للأسف لم يتم قبول طلبك في هذه المرة.'
                      : 'Unfortunately your request was not accepted this time.'
                    )}
                  </p>

                  {/* Request number badge */}
                  {(existingRequest.request_number || existingRequest.id) && (
                    <div className={`mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono ${
                      isDarkMode ? 'bg-white/5 border border-white/10 text-gray-300' : 'bg-gray-100 border border-gray-200 text-gray-700'
                    }`} data-testid="agent-request-number">
                      <span className={isDarkMode ? 'text-gray-500' : 'text-gray-500'}>
                        {language === 'ar' ? 'رقم الطلب:' : 'Request No:'}
                      </span>
                      <span className="font-bold">
                        {existingRequest.request_number || existingRequest.id?.slice(0, 8).toUpperCase()}
                      </span>
                    </div>
                  )}
                  
                  {/* Show rejection notes if available */}
                  {existingRequest.status === 'rejected' && existingRequest.rejection_notes && (
                    <div className={`mt-3 p-3 rounded-lg border ${
                      isDarkMode ? 'bg-red-500/10 border-red-500/30' : 'bg-red-100 border-red-200'
                    }`}>
                      <p className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-red-400' : 'text-red-800'}`}>
                        {language === 'ar' ? 'سبب الرفض:' : 'Rejection Reason:'}
                      </p>
                      <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>{existingRequest.rejection_notes}</p>
                    </div>
                  )}
                  
                  {/* Show reapply info for rejected requests — only when cooldown is enabled */}
                  {existingRequest.status === 'rejected' && cooldownActive && (
                    <div className={`mt-3 p-3 rounded-lg border ${
                      isDarkMode ? 'bg-[#38BDF8]/10 border-[#38BDF8]/30' : 'bg-blue-50 border-blue-200'
                    }`}>
                      <p className={`text-sm ${isDarkMode ? 'text-[#38BDF8]' : 'text-blue-800'}`}>
                        {language === 'ar' 
                          ? `📅 يمكنك التقديم مرة أخرى بعد ${cooldown.days} ${cooldown.days === 1 ? 'يوم' : cooldown.days === 2 ? 'يومان' : 'أيام'} من تاريخ الرفض، أو يمكنك التسجيل فوراً باستخدام كود دعوة من الشركة.`
                          : `📅 You can reapply after ${cooldown.days} day${cooldown.days === 1 ? '' : 's'} from the rejection date, or you can register immediately using a company invitation code.`
                        }
                      </p>
                      {existingRequest.rejected_at && cooldownEndMs && (
                        <p className={`text-xs mt-2 ${isDarkMode ? 'text-[#38BDF8]/70' : 'text-blue-600'}`}>
                          {language === 'ar' 
                            ? `تاريخ الرفض: ${new Date(existingRequest.rejected_at).toLocaleDateString('ar-AE')} - يمكنك التقديم بعد: ${new Date(cooldownEndMs).toLocaleDateString('ar-AE')}`
                            : `Rejected on: ${new Date(existingRequest.rejected_at).toLocaleDateString('en-US')} - Can reapply after: ${new Date(cooldownEndMs).toLocaleDateString('en-US')}`
                          }
                        </p>
                      )}
                    </div>
                  )}

                  {/* Cooldown disabled → invite user to reapply immediately */}
                  {existingRequest.status === 'rejected' && !cooldownActive && (
                    <div className={`mt-3 p-3 rounded-lg border ${
                      isDarkMode ? 'bg-green-500/10 border-green-500/30' : 'bg-green-50 border-green-200'
                    }`}>
                      <p className={`text-sm ${isDarkMode ? 'text-green-300' : 'text-green-800'}`}>
                        {language === 'ar'
                          ? '✅ يمكنك التقديم مرة أخرى الآن — لا توجد فترة انتظار.'
                          : '✅ You can reapply now — no waiting period required.'
                        }
                      </p>
                    </div>
                  )}
                  
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                      isDarkMode ? 'bg-white/10 text-gray-300' : 'bg-white/70'
                    }`}>
                      {language === 'ar' ? 'تاريخ التقديم:' : 'Submitted:'} {new Date(existingRequest.created_at).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US')}
                    </span>
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                      existingRequest.status === 'pending' 
                        ? 'bg-orange-200 text-orange-800'
                        : existingRequest.status === 'approved'
                        ? 'bg-green-200 text-green-800'
                        : 'bg-red-200 text-red-800'
                    }`}>
                      {existingRequest.status === 'pending' && (language === 'ar' ? 'قيد المراجعة' : 'Pending Review')}
                      {existingRequest.status === 'approved' && (language === 'ar' ? 'مقبول' : 'Approved')}
                      {existingRequest.status === 'rejected' && (language === 'ar' ? 'مرفوض' : 'Rejected')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Agent Benefits Info - Elegant Card */}
        <div className={`mb-6 rounded-2xl p-6 shadow-xl relative overflow-hidden ${
          isDarkMode 
            ? 'bg-[#131B2C] border border-white/10' 
            : 'bg-gradient-to-br from-[#1E40AF] to-[#3B82F6] text-white'
        }`}>
          {/* Subtle accent line */}
          <div className={`absolute top-0 start-0 end-0 h-1 ${
            isDarkMode ? 'bg-gradient-to-r from-[#38BDF8] to-[#38BDF8]/0' : 'bg-white/20'
          }`} />
          
          <h2 className={`relative text-xl font-bold mb-4 flex items-center gap-2 ${
            isDarkMode ? 'text-white' : ''
          }`}>
            <Award className={`h-6 w-6 ${isDarkMode ? 'text-amber-400' : 'text-amber-300'}`} />
            {language === 'ar' ? 'مميزات الوكيل المعتمد' : 'Authorized Agent Benefits'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`rounded-xl p-4 ${
              isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white/10 backdrop-blur-sm'
            }`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-[#38BDF8]/20' : 'bg-white/20'}`}>
                  <Target className={`h-5 w-5 ${isDarkMode ? 'text-[#38BDF8]' : ''}`} />
                </div>
                <h3 className={`font-bold ${isDarkMode ? 'text-white' : ''}`}>{language === 'ar' ? 'مهام مخصصة' : 'Dedicated Tasks'}</h3>
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-white/80'}`}>
                {language === 'ar' 
                  ? 'ستحصل على مهام مخصصة من CloudGM تناسب مهاراتك وخبراتك في مجال الأعمال'
                  : 'You will receive dedicated tasks from CloudGM that match your skills and business expertise'
                }
              </p>
            </div>
            
            <div className={`rounded-xl p-4 ${
              isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white/10 backdrop-blur-sm'
            }`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-emerald-500/20' : 'bg-white/20'}`}>
                  <DollarSign className={`h-5 w-5 ${isDarkMode ? 'text-emerald-400' : ''}`} />
                </div>
                <h3 className={`font-bold ${isDarkMode ? 'text-white' : ''}`}>{language === 'ar' ? 'عمولات مجزية' : 'Attractive Commission'}</h3>
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-white/80'}`}>
                {language === 'ar' 
                  ? 'احصل على نسبة عمولة قد تصل إلى 15% على كل صفقة ناجحة تتم من خلالك'
                  : 'Earn commission up to 15% on every successful deal you close'
                }
              </p>
            </div>
            
            <div className={`rounded-xl p-4 ${
              isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white/10 backdrop-blur-sm'
            }`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-[#1E9BD9]/20' : 'bg-white/20'}`}>
                  <TrendingUp className={`h-5 w-5 ${isDarkMode ? 'text-[#1E9BD9]' : ''}`} />
                </div>
                <h3 className={`font-bold ${isDarkMode ? 'text-white' : ''}`}>{language === 'ar' ? 'نمو مستمر' : 'Continuous Growth'}</h3>
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-white/80'}`}>
                {language === 'ar' 
                  ? 'فرص للتطور والترقي مع زيادة حجم المهام والمسؤوليات'
                  : 'Opportunities for advancement with increasing tasks and responsibilities'
                }
              </p>
            </div>
            
            <div className={`rounded-xl p-4 ${
              isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white/10 backdrop-blur-sm'
            }`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-amber-500/20' : 'bg-white/20'}`}>
                  <ShieldCheck className={`h-5 w-5 ${isDarkMode ? 'text-amber-400' : ''}`} />
                </div>
                <h3 className={`font-bold ${isDarkMode ? 'text-white' : ''}`}>{language === 'ar' ? 'دعم كامل' : 'Full Support'}</h3>
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-white/80'}`}>
                {language === 'ar' 
                  ? 'دعم فني ومتابعة مستمرة من فريق CloudGM لضمان نجاحك'
                  : 'Technical support and continuous follow-up from CloudGM team'
                }
              </p>
            </div>
          </div>
          
          <div className={`mt-4 p-3 rounded-xl text-center ${
            isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white/20'
          }`}>
            <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : ''}`}>
              {language === 'ar' 
                ? '💼 انضم الآن لفريق الوكلاء المعتمدين وابدأ رحلتك في عالم الأعمال'
                : '💼 Join our authorized agents team now and start your business journey'
              }
            </p>
          </div>
        </div>

        {/* Agent Card Preview */}
        <div className={`mb-6 rounded-2xl shadow-lg overflow-hidden ${
          isDarkMode ? 'bg-[#131B2C] border border-white/10' : 'bg-white'
        }`}>
          <div className={`p-4 sm:p-6 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
            <h3 className={`text-lg font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              <Award className="h-5 w-5 text-amber-500" />
              {language === 'ar' ? 'بطاقة الوكيل المعتمد' : 'Authorized Agent Card'}
            </h3>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {language === 'ar' 
                ? 'بعد الموافقة على طلبك، ستحصل على بطاقة وكيل معتمد مثل هذه'
                : 'After approval, you will receive an authorized agent card like this'}
            </p>
          </div>
          <div className={`p-4 sm:p-6 flex justify-center ${
            isDarkMode ? 'bg-gradient-to-br from-[#1E293B] to-[#0F172A]' : 'bg-gradient-to-br from-gray-50 to-gray-100'
          }`}>
            <img 
              src="https://customer-assets.emergentagent.com/job_cloudgm-hub-1/artifacts/gl0g7d1v_agant.png" 
              alt="Agent Card Preview" 
              className="max-w-full sm:max-w-md rounded-xl shadow-lg hover:scale-105 transition-transform duration-300"
            />
          </div>
          <div className={`p-4 border-t ${
            isDarkMode ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-100'
          }`}>
            <p className={`text-sm text-center ${isDarkMode ? 'text-amber-400' : 'text-amber-800'}`}>
              {language === 'ar' 
                ? '⭐ البطاقة تحتوي على رقم الوكيل الفريد الخاص بك (BGM) ومعلوماتك المعتمدة'
                : '⭐ The card contains your unique agent number (BGM) and verified information'}
            </p>
          </div>
        </div>

        {/* Registration Type Selection - Show if no existing request OR if rejected (allow invitation code) */}
        {((!existingRequest || existingRequest.status === 'rejected') && !registrationType) ? (
          <div className={`rounded-2xl shadow-lg p-6 sm:p-8 ${
            isDarkMode ? 'bg-[#131B2C] border border-white/10' : 'bg-white'
          }`}>
            <h2 className={`text-xl font-bold mb-6 text-center ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              {language === 'ar' ? 'كيف تريد التسجيل؟' : 'How would you like to register?'}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* New Agent — hidden while cooldown is active */}
              {(!existingRequest || existingRequest.status !== 'rejected' || !isInCooldown) && (
              <button
                onClick={() => setRegistrationType('new')}
                className={`p-6 rounded-xl border-2 transition-all text-center group ${
                  isDarkMode 
                    ? 'border-white/10 hover:border-[#38BDF8]/50 hover:bg-[#38BDF8]/10' 
                    : 'border-gray-200 hover:border-[#38BDF8] hover:bg-[#38BDF8]/10'
                }`}
              >
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center transition-colors ${
                  isDarkMode 
                    ? 'bg-[#38BDF8]/20 group-hover:bg-[#38BDF8]/30' 
                    : 'bg-[#38BDF8]/10 group-hover:bg-[#38BDF8]/20'
                }`}>
                  <FileText className="h-8 w-8 text-[#38BDF8]" />
                </div>
                <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  {language === 'ar' ? 'تسجيل جديد' : 'New Registration'}
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>
                  {language === 'ar' 
                    ? 'سجل كوكيل جديد وارفع سيرتك الذاتية للمراجعة'
                    : 'Register as a new agent and upload your CV for review'
                  }
                </p>
              </button>
              )}

              {/* Disabled card with a live day-countdown while cooldown is active */}
              {isInCooldown && (
              <div
                className={`p-6 rounded-xl border-2 text-center opacity-70 cursor-not-allowed ${
                  isDarkMode ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'
                }`}
                data-testid="new-registration-cooldown"
              >
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                  isDarkMode ? 'bg-white/10' : 'bg-gray-200'
                }`}>
                  <Clock className={`h-8 w-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                </div>
                <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {language === 'ar' ? 'تسجيل جديد' : 'New Registration'}
                </h3>
                <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold mt-1 ${
                  isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700'
                }`} data-testid="cooldown-days-remaining">
                  <Clock className="h-4 w-4" />
                  {daysRemainingLabel}
                </div>
                <p className={`text-xs mt-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  {language === 'ar' 
                    ? `متاح بعد انتهاء فترة الانتظار (${cooldown.days} ${cooldown.days === 1 ? 'يوم' : cooldown.days === 2 ? 'يومان' : 'أيام'})`
                    : `Available after waiting period (${cooldown.days} day${cooldown.days === 1 ? '' : 's'})`
                  }
                </p>
              </div>
              )}

              {/* Invited Agent - Always available */}
              <button
                onClick={() => setRegistrationType('invited')}
                className={`p-6 rounded-xl border-2 transition-all text-center group ${
                  isDarkMode 
                    ? 'border-white/10 hover:border-[#F97316]/50 hover:bg-[#F97316]/10' 
                    : 'border-gray-200 hover:border-[#F97316] hover:bg-[#F97316]/10'
                }`}
              >
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center transition-colors ${
                  isDarkMode 
                    ? 'bg-[#F97316]/20 group-hover:bg-[#F97316]/30' 
                    : 'bg-[#F97316]/10 group-hover:bg-[#F97316]/20'
                }`}>
                  <Briefcase className="h-8 w-8 text-[#F97316]" />
                </div>
                <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  {language === 'ar' ? 'دعوة من الشركة' : 'Company Invitation'}
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>
                  {language === 'ar' 
                    ? 'لديك كود دعوة من CloudGM؟ أدخله هنا'
                    : 'Have an invitation code from CloudGM? Enter it here'
                  }
                </p>
                {existingRequest?.status === 'rejected' && (
                  <span className="mt-2 inline-block px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                    {language === 'ar' ? 'متاح الآن' : 'Available Now'}
                  </span>
                )}
              </button>
            </div>
          </div>
        ) : ((!existingRequest || existingRequest.status === 'rejected') && registrationType) ? (
          /* Registration Form */
          <div className={`rounded-2xl shadow-lg overflow-hidden ${
            isDarkMode ? 'bg-[#131B2C] border border-white/10' : 'bg-white'
          }`}>
            {/* Form Header */}
            <div className={`p-6 border-b ${
              isDarkMode 
                ? 'bg-gradient-to-r from-[#38BDF8]/10 to-[#F97316]/10 border-white/10' 
                : 'bg-gradient-to-r from-[#38BDF8]/10 to-[#F97316]/10'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {registrationType === 'new' ? (
                    <FileText className="h-6 w-6 text-[#38BDF8]" />
                  ) : (
                    <Briefcase className="h-6 w-6 text-[#F97316]" />
                  )}
                  <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    {registrationType === 'new'
                      ? (language === 'ar' ? 'تسجيل وكيل جديد' : 'New Agent Registration')
                      : (language === 'ar' ? 'تسجيل بكود الدعوة' : 'Register with Invitation Code')
                    }
                  </h2>
                </div>
                <button
                  onClick={() => setRegistrationType(null)}
                  className={`text-sm ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-[#475569] hover:text-[#0F172A]'}`}
                >
                  {language === 'ar' ? 'تغيير' : 'Change'}
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Personal Info Section */}
              <div className="space-y-4">
                <h3 className={`font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  <User className="h-5 w-5 text-[#38BDF8]" />
                  {language === 'ar' ? 'المعلومات الشخصية' : 'Personal Information'}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>{language === 'ar' ? 'الاسم الكامل (عربي) *' : 'Full Name (Arabic) *'}</Label>
                    <Input
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      className="mt-2"
                      dir="rtl"
                    />
                  </div>
                  <div>
                    <Label>{language === 'ar' ? 'الاسم الكامل (إنجليزي) *' : 'Full Name (English) *'}</Label>
                    <Input
                      value={formData.fullNameEn}
                      onChange={handleEnglishNameChange}
                      className={`mt-2 ${englishNameError ? 'border-red-500 focus:ring-red-500' : ''}`}
                      dir="ltr"
                      placeholder={language === 'ar' ? 'مثال: Mohammed Ahmed' : 'e.g., Mohammed Ahmed'}
                    />
                    {englishNameError && (
                      <p className="text-xs text-red-500 mt-1">{englishNameError}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {language === 'ar' ? 'البريد الإلكتروني *' : 'Email *'}
                    </Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-2"
                      dir="ltr"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {language === 'ar' ? 'رقم الهاتف *' : 'Phone Number *'}
                    </Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="mt-2"
                      dir="ltr"
                    />
                  </div>
                </div>
              </div>

              {/* Languages Section */}
              <div className="space-y-4">
                <h3 className={`font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  <Globe className="h-5 w-5 text-[#38BDF8]" />
                  {language === 'ar' ? 'اللغات التي تجيدها *' : 'Languages You Speak *'}
                </h3>
                
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {availableLanguages.map((lang) => (
                    <button
                      key={lang.id}
                      onClick={() => toggleLanguage(lang.id)}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        formData.languages.includes(lang.id)
                          ? 'border-[#38BDF8] bg-[#38BDF8]/10 text-[#38BDF8]'
                          : isDarkMode 
                            ? 'border-white/10 hover:border-white/20 text-gray-300' 
                            : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {formData.languages.includes(lang.id) && (
                        <CheckCircle className="h-4 w-4 mx-auto mb-1" />
                      )}
                      <p className="text-sm font-medium">
                        {language === 'ar' ? lang.name : lang.nameEn}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Skills — chip input, shared by both flows */}
              <div>
                <Label className={isDarkMode ? 'text-gray-300' : ''}>
                  {language === 'ar' ? 'المهارات *' : 'Skills *'}
                </Label>
                <p className={`text-xs mt-1 mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  {language === 'ar' ? 'أضف مهاراتك (اضغط Enter بعد كل مهارة)' : 'Add your skills (press Enter after each)'}
                </p>
                <div className={`flex flex-wrap gap-2 min-h-[52px] p-2 rounded-lg border ${
                  isDarkMode ? 'bg-[#0D1421] border-white/10' : 'border-gray-200'
                }`}>
                  {formData.skills.map((skill, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm bg-[#38BDF8]/10 text-[#38BDF8] border border-[#38BDF8]/30"
                      data-testid={`skill-chip-${i}`}
                    >
                      {skill}
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, skills: formData.skills.filter((_, idx) => idx !== i) })}
                        className="hover:text-red-500"
                        aria-label="remove"
                      >
                        <XCircle className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ',') && skillInput.trim()) {
                        e.preventDefault();
                        const s = skillInput.trim();
                        if (!formData.skills.includes(s) && formData.skills.length < 15) {
                          setFormData({ ...formData, skills: [...formData.skills, s] });
                        }
                        setSkillInput('');
                      } else if (e.key === 'Backspace' && !skillInput && formData.skills.length) {
                        setFormData({ ...formData, skills: formData.skills.slice(0, -1) });
                      }
                    }}
                    placeholder={language === 'ar' ? 'مثال: مبيعات، تسويق، إدارة عقود...' : 'e.g. Sales, Marketing, Contracts...'}
                    className={`flex-1 min-w-[160px] bg-transparent outline-none text-sm ${
                      isDarkMode ? 'text-white placeholder:text-gray-500' : 'text-gray-900 placeholder:text-gray-400'
                    }`}
                    data-testid="skill-input"
                  />
                </div>

                {/* Suggested skills — click to add */}
                {(() => {
                  const suggestionsAr = [
                    'مبيعات', 'تسويق رقمي', 'إدارة عقود', 'خدمة عملاء', 'إدارة مشاريع',
                    'تفاوض', 'محاسبة', 'موارد بشرية', 'تصميم جرافيك', 'كتابة محتوى',
                    'إدارة سوشيال ميديا', 'دعم فني', 'ترجمة', 'استشارات قانونية', 'تحليل بيانات'
                  ];
                  const suggestionsEn = [
                    'Sales', 'Digital Marketing', 'Contracts', 'Customer Service', 'Project Management',
                    'Negotiation', 'Accounting', 'HR', 'Graphic Design', 'Copywriting',
                    'Social Media', 'Tech Support', 'Translation', 'Legal Advisory', 'Data Analysis'
                  ];
                  const list = language === 'ar' ? suggestionsAr : suggestionsEn;
                  const available = list.filter(s => !formData.skills.includes(s));
                  if (available.length === 0) return null;
                  return (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <span className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {language === 'ar' ? 'اقتراحات:' : 'Suggestions:'}
                      </span>
                      {available.slice(0, 10).map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => {
                            if (formData.skills.length < 15) {
                              setFormData({ ...formData, skills: [...formData.skills, s] });
                            }
                          }}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                            isDarkMode
                              ? 'border-white/10 text-gray-400 hover:bg-[#38BDF8]/10 hover:border-[#38BDF8]/40 hover:text-[#38BDF8]'
                              : 'border-gray-200 text-gray-600 hover:bg-[#38BDF8]/5 hover:border-[#38BDF8]/40 hover:text-[#38BDF8]'
                          }`}
                          data-testid={`skill-suggestion-${s}`}
                        >
                          + {s}
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Experience */}
              <div>
                <Label className={isDarkMode ? 'text-gray-300' : ''}>{language === 'ar' ? 'الخبرة والمهارات' : 'Experience & Skills'}</Label>
                <textarea
                  value={formData.experience}
                  onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  placeholder={language === 'ar' ? 'اكتب نبذة عن خبرتك ومهاراتك...' : 'Tell us about your experience and skills...'}
                  className={`mt-2 w-full px-3 py-2 border rounded-lg resize-none h-24 focus:outline-none focus:ring-2 focus:ring-[#38BDF8]/20 focus:border-[#38BDF8] ${
                    isDarkMode 
                      ? 'bg-[#0D1421] border-white/10 text-white placeholder:text-gray-500' 
                      : 'border-gray-200'
                  }`}
                />
              </div>

              {/* Conditional: CV Upload or Invitation Code */}
              {registrationType === 'new' ? (
                <div className="space-y-4">
                  <h3 className={`font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    <Upload className="h-5 w-5 text-[#38BDF8]" />
                    {language === 'ar' ? 'السيرة الذاتية *' : 'CV / Resume *'}
                  </h3>
                  
                  <div 
                    onClick={() => cvInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                      isDarkMode 
                        ? 'border-white/20 hover:border-[#38BDF8]' 
                        : 'border-gray-300 hover:border-[#38BDF8]'
                    }`}
                  >
                    {cvFileName ? (
                      <div className="flex items-center justify-center gap-3">
                        <FileText className="h-8 w-8 text-green-500" />
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{cvFileName}</p>
                          <p className="text-sm text-green-600">
                            {language === 'ar' ? 'تم رفع الملف بنجاح' : 'File uploaded successfully'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <Upload className={`h-10 w-10 mx-auto mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        <p className={isDarkMode ? 'text-gray-400' : 'text-[#475569]'}>
                          {language === 'ar' ? 'اضغط لرفع سيرتك الذاتية' : 'Click to upload your CV'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          PDF, DOC, DOCX - {language === 'ar' ? 'حتى 10 ميجابايت' : 'up to 10MB'}
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    ref={cvInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleCvUpload}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  <h3 className={`font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    <Briefcase className="h-5 w-5 text-[#F97316]" />
                    {language === 'ar' ? 'كود الدعوة *' : 'Invitation Code *'}
                  </h3>
                  
                  <Input
                    value={formData.invitationCode}
                    onChange={(e) => setFormData({ ...formData, invitationCode: e.target.value.toUpperCase() })}
                    placeholder={language === 'ar' ? 'أدخل كود الدعوة' : 'Enter invitation code'}
                    className={`text-center text-lg font-mono tracking-widest ${
                      isDarkMode ? 'bg-[#0D1421] border-white/10 text-white' : ''
                    }`}
                    dir="ltr"
                  />
                  <p className={`text-xs text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {language === 'ar' 
                      ? 'أدخل الكود الذي تلقيته من فريق CloudGM'
                      : 'Enter the code you received from CloudGM team'
                    }
                  </p>

                  {/* Email match tooltip — prevents 403 email mismatch errors */}
                  <div className={`flex items-start gap-2.5 rounded-xl p-3 border ${
                    isDarkMode 
                      ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' 
                      : 'bg-amber-50 border-amber-200 text-amber-800'
                  }`} data-testid="invitation-email-match-tip">
                    <AlertCircle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                    <p className="text-xs leading-relaxed">
                      {language === 'ar'
                        ? 'يجب أن يكون حسابك مسجَّلاً بنفس البريد الإلكتروني الذي أرسلت له الإدارة رمز الدعوة، وإلا لن يُقبل الكود.'
                        : 'Your account must be registered with the same email that received the invitation code, otherwise it will be rejected.'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className={`p-6 border-t flex gap-3 ${
              isDarkMode ? 'bg-white/5 border-white/10' : 'bg-gray-50'
            }`}>
              <Button 
                variant="outline" 
                className={`flex-1 ${isDarkMode ? 'border-white/10 text-gray-300 hover:bg-white/5' : ''}`}
                onClick={() => navigate('/dashboard')}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={submitting}
                className={`flex-1 text-white ${
                  isDarkMode 
                    ? 'bg-[#38BDF8] hover:bg-[#0EA5E9]' 
                    : 'bg-[#1E40AF] hover:bg-[#1E3A8A]'
                }`}
              >
                {submitting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  language === 'ar' ? 'إرسال الطلب' : 'Submit Request'
                )}
              </Button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
